<template>
  <v-dialog
    v-model="dialog"
    :max-width="options.width"
    :style="{ zIndex: options.zIndex }"
    @keydown.esc="cancel"
  >
    <v-card>
      <v-container class="px-10 pt-8">
        <p class="subtitle-1 font-weight-bold mb-2">Confirm Payment</p>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="3">
            <p class="caption">Tenant:</p>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="9">
            <p class="caption font-weight-bold">{{ payment.tenantName }}</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="3">
            <p class="caption">Balance:</p>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="9">
            <p class="caption font-weight-bold">
              {{ formatCurrency(-tenantBalance) }}
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="py-0" cols="12" sm="12" md="3">
            <p class="caption">Total:</p>
          </v-col>
          <v-col class="py-0" cols="12" sm="12" md="9">
            <p class="caption font-weight-bold">
              {{ formatCurrency(total) }}
            </p>
          </v-col>
        </v-row>
        <v-divider class="my-4"></v-divider>
        <v-form v-model="isFormValid">
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-currency-field
                v-model="amount"
                type="number"
                label="Amount To Pay"
                prepend-inner-icon="mdi-cash"
                disabled
                :hint="useBalanceHint"
                persistent-hint
                outlined
                dense
              >
              </v-currency-field>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" md="6">
              <v-currency-field
                v-model="amountPaid"
                type="number"
                label="Amount Paid"
                prepend-inner-icon="mdi-cash"
                :rules="amountRules"
                outlined
                dense
              >
              </v-currency-field>
            </v-col>
          </v-row>
          <v-row v-if="tenantBalance > 0">
            <v-col class="pt-0 pb-2" cols="12" sm="12" md="12">
              <v-checkbox
                v-model="useRemainingBalance"
                :label="useBalanceLabel"
                class="my-0"
                hide-details
                :disabled="!tenantBalance"
                @change="useRemainingBalanceEvent"
              ></v-checkbox>
            </v-col>
          </v-row>

          <v-expansion-panels
            v-if="showSecurityDeposit"
            class="pb-2 my-2"
            accordion
            flat
          >
            <v-expansion-panel>
              <v-expansion-panel-header class="py-2 body-2"
                >Security deposit found. Click here for more
                details</v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <v-divider class="mb-0"></v-divider>
                <!-- Available security deposits -->
                <v-list-item
                  v-for="securityDeposit in securityDeposits"
                  :key="securityDeposit.securityDepositId"
                  three-line
                >
                  <v-list-item-avatar size="30">
                    <v-icon class="grey lighten-1" small dark>
                      mdi-cash
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="body-2">
                      Total amount:
                      {{
                        formatCurrency(securityDeposit.amount)
                      }}</v-list-item-title
                    >
                    <v-list-item-subtitle
                      v-if="securityDeposit.description"
                      class="caption"
                    >
                      {{ securityDeposit.description }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else class="caption grey--text">
                      No description provided
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="caption">
                      Usable amount:
                      {{ formatCurrency(securityDeposit.usable) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mb-1"></v-divider>

                <div class="mb-4 d-flex flex-row justify-space-between">
                  <p class="caption">Total usable security deposit:</p>
                  <p class="font-weight-bold">
                    {{ formatCurrency(maxSecurityDepositAmount) }}
                  </p>
                </div>

                <v-row>
                  <v-col class="py-0 pb-2" cols="12" sm="12" md="12">
                    <v-switch
                      v-model="useSecurityDeposit"
                      class="my-0 mb-2"
                      label="Use security deposit?"
                      hide-details
                      inset
                      dense
                      @change="useRemainingBalanceEvent"
                    ></v-switch>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0" cols="12" sm="12" md="12">
                    <v-currency-field
                      v-model="securityDepositAmountUsed"
                      label="Deducted amount"
                      :disabled="!useSecurityDeposit"
                      :rules="
                        useSecurityDeposit
                          ? securityDepositAmountRules
                          : undefined
                      "
                      outlined
                      dense
                    >
                    </v-currency-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <v-row>
            <v-col class="py-1" cols="12" sm="12" md="12">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="date"
                    label="Date Paid"
                    prepend-inner-icon="mdi-calendar-month"
                    v-bind="attrs"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="date"
                  no-title
                  scrollable
                  @input="dateMenu = false"
                >
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-1" cols="12" sm="12" md="12">
              <v-textarea
                v-model="description"
                label="(Optional) Add a description"
                hide-details
                dense
                outlined
                class="my-1"
                rows="3"
              >
              </v-textarea>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0" cols="12" sm="12" md="12">
              <v-file-input
                v-model="attachments"
                accept="image/*, application/pdf"
                label="Upload Attachments"
                prepend-icon=""
                chips
                small-chips
                multiple
                outlined
                dense
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon small class="mt-1">mdi-paperclip</v-icon>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
          <p v-if="currentAttachments.length">Attachments</p>
          <v-chip-group column multiple>
            <v-chip
              v-for="(item, index) in currentAttachments"
              :key="index"
              close
              @click:close="removeAttachment(item, index)"
            >
              {{ truncate(item.filename) }}
            </v-chip>
          </v-chip-group>
          <v-row>
            <v-col class="pb-2" cols="12" sm="12" md="12">
              <v-checkbox v-model="confirmSendEmail" class="my-0" hide-details>
                <template v-slot:label> Send email to tenant </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="font-weight-bold"
            color="primary darken-1"
            text
            :disabled="!isFormValid"
            @click="finish"
            >Finish</v-btn
          >
          <v-btn
            class="font-weight-bold"
            color="grey darken-1"
            text
            @click="cancel"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-container>
    </v-card>
    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import { dayjs } from '../../util/date'
import { getCurrencyFieldValue } from '../../util/currency'

export default {
  data() {
    return {
      dialog: false,
      options: {
        color: 'primary',
        width: 500,
        zIndex: 200
      },
      date: dayjs().startOf('day').format('YYYY-MM-DD'),
      datePicker: dayjs().startOf('day').format('YYYY-MM-DD'),
      dateMenu: false,
      // amount to pay
      amount: 0,
      // amount paid
      amountPaid: 0,
      loading: true,
      total: 0,
      tenantBalance: 0,
      balanceUsed: 0,
      useRemainingBalance: false,
      useBalanceHint: '',
      amountRules: [
        (v) => (v != null && v != undefined) || 'Amount is Required.',
        (v) => Number.isInteger(parseInt(v)) || 'Amount must be a number.'
        // (v) => {
        //   if (!this.useSecurityDeposit) return true
        //   return (
        //     getCurrencyFieldValue(v) + (this.securityDepositAmountUsed || 0) <=
        //       this.amount ||
        //     'Consumed security deposit cannot exceed the amount to pay'
        //   )
        // }
      ],
      isFormValid: true,
      attachments: [],
      // for existing partial payments
      currentAmountPaid: 0,
      currentAttachments: [],
      confirmSendEmail: false,
      description: '',
      securityDeposits: [],
      showSecurityDeposit: false,
      useSecurityDeposit: false,
      securityDepositAmountUsed: null,
      maxSecurityDepositAmount: null,
      securityDepositAmountRules: [
        (v) => {
          return (
            getCurrencyFieldValue(v) <= this.maxSecurityDepositAmount ||
            'Cannot exceed the usable security deposit amount'
          )
        }
        // (v) =>
        //   getCurrencyFieldValue(v) + this.amountPaid <= this.amount ||
        //   'Consumed security deposit cannot exceed the amount to pay'
      ],
      payment: {}
    }
  },
  computed: {
    useBalanceLabel() {
      // if(this.tenantBalance >= 0) {
      return `Use Remaining Credits`
      // }
      // else {
      //   return `Include Outstanding Balance`
      // }
    }
  },
  methods: {
    async open(payment) {
      this.currentAmountPaid =
        payment.amountPaid -
          (payment.adjustments || []).reduce((a, b) => a + b.value, 0) || 0
      this.loading = true
      this.dialog = true
      this.total = parseFloat(payment.total)
      this.payment = payment
      this.amount = payment.total - this.currentAmountPaid
      this.amountPaid = payment.total - this.currentAmountPaid
      this.useBalanceHint = ''
      this.currentAttachments = payment.attachments || []
      this.attachments = []
      this.confirmSendEmail = false
      this.useSecurityDeposit = false
      // this.tenantBalance = parseFloat(payment.tenantBalance)
      this.tenantBalance = await this.$store.dispatch(
        'units/fetchTenantBalanceAction',
        {
          tenantId: payment.tenantId
        }
      )
      if (this.tenantBalance > 0) {
        this.useRemainingBalance = true
        this.balanceUsed =
          this.tenantBalance - this.amount >= 0
            ? this.amount
            : this.tenantBalance
        this.useBalanceHint =
          this.tenantBalance > 0
            ? 'Amount reduced by ' + this.formatCurrency(this.balanceUsed)
            : 'Added debit of ' + this.formatCurrency(-this.balanceUsed)
        this.amount = this.total - this.balanceUsed - this.currentAmountPaid
        this.amountPaid = this.total - this.balanceUsed - this.currentAmountPaid
      } else {
        this.useRemainingBalance = false
        this.balanceUsed = 0
        this.useBalanceHint = ''
      }

      this.fetchSecurityDeposit()
      this.loading = false
      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    async finish() {
      try {
        // let difference = parseFloat(this.amount) - parseFloat(this.amountPaid)
        let confirmMessage = `Are you sure that ${
          this.payment.tenantName
        } has paid ${this.formatCurrency(this.amountPaid)}?`
        // if(difference > 0) {
        //   confirmMessage = `The total amount is less than the required payment. This will DEBIT ${this.formatCurrency(difference)} from the tenant's account. Continue?`
        // }
        // else if (difference < 0) {
        //   confirmMessage = `The total amount is more than the required payment. This will CREDIT ${this.formatCurrency(Math.abs(difference))} to the tenant's account. Continue?`
        // }
        this.loading = true
        if (
          await this.$root.$confirm({
            title: 'Confirm payment',
            message: confirmMessage
          })
        ) {
          if (
            await this.$store.dispatch('payments/setPaidAction', {
              paymentId: this.payment.paymentId,
              amountPaid: this.amountPaid,
              datePaid: dayjs(this.date).format(),
              balanceUsed: this.balanceUsed,
              total: this.total,
              attachments: this.attachments.filter(
                (attachment) => attachment instanceof File
              ),
              currentAttachments: this.currentAttachments,
              confirmSendEmail: this.confirmSendEmail,
              description: this.description,
              deductFromSecDep: this.useSecurityDeposit
                ? this.securityDepositAmountUsed
                : undefined
            })
          ) {
            this.$root
              .$confirm({
                title: 'Confirm Payment',
                message: `The payment has been confirmed.`,
                reconfirmOnly: true,
                confirmText: 'OK'
              })
              .then(() => {
                this.loading = false
                this.dialog = false
                this.resolve(this.payment.paymentId)
                // window.location.reload()
              })
          }
        }
      } catch (err) {
        this.$root.$confirm({
          title: 'Confirm Payment Error',
          message: err,
          reconfirmOnly: true,
          confirmText: 'OK'
        })
      }
      this.loading = false
    },
    cancel() {
      this.dialog = false
      this.loading = false
    },
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    },
    useRemainingBalanceEvent() {
      // set the base value of amount and amound paid
      this.amount = this.total - this.currentAmountPaid
      this.amountPaid = this.amount
      let reduced = 0

      if (!this.useRemainingBalance && !this.useSecurityDeposit) {
        this.balanceUsed = 0
        this.useBalanceHint = ''
        this.amount = this.total - this.currentAmountPaid
        this.amountPaid = this.total - this.currentAmountPaid
        return
      }

      // this.useRemainingBalance = !this.useRemainingBalance
      if (this.useRemainingBalance) {
        this.balanceUsed = Math.min(this.tenantBalance, this.amount)
        reduced += this.balanceUsed

        this.amount = this.amount - this.balanceUsed
        this.amountPaid = this.amountPaid - this.balanceUsed
      }

      if (this.useSecurityDeposit) {
        this.securityDepositAmountUsed = Math.min(
          this.maxSecurityDepositAmount,
          this.amount
        )
        reduced += this.securityDepositAmountUsed
        this.amount -= this.securityDepositAmountUsed
        this.amountPaid -= this.securityDepositAmountUsed
      }

      this.useBalanceHint =
        reduced > 0
          ? 'Amount reduced by ' + this.formatCurrency(reduced)
          : 'Added debit of ' + this.formatCurrency(-reduced)
    },
    async removeAttachment(item, index) {
      if (
        await this.$root.$confirm({
          title: 'Remove attachment',
          message: `Are you sure you would like to delete ${item.filename}?`
        })
      ) {
        this.currentAttachments.splice(index, 1)
      }
    },
    truncate(input) {
      if (input.length > 7) {
        return input.substring(0, 7) + '...'
      }
      return input
    },
    async fetchSecurityDeposit() {
      const { usableAmount, securityDeposits } = await this.$store.dispatch(
        'securityDeposits/fetchSecurityDepositAction',
        {
          leaseId: this.payment.leaseId,
          tenantId: this.payment.tenantId
        }
      )

      if (!securityDeposits || !usableAmount) {
        return
      }

      this.securityDeposits = securityDeposits
      this.showSecurityDeposit = true
      this.maxSecurityDepositAmount = usableAmount
      this.securityDepositAmountUsed = Math.min(usableAmount, this.amount)
    }
  }
}
</script>

<style scoped>
p {
  margin-bottom: 0;
}
.v-card.card-option {
  border-radius: 20px !important;
}

.v-expansion-panel {
  border-style: solid;
  border-width: 1px;
  border-color: rgba(0, 0, 0, 0.12);
}
</style>
